// src/services/dtrLockDerivation.js
//
// Derived DTR locks (PRD_DtrWriteModel C1). Lock state is NO LONGER stamped on
// each DTR record — it is computed at read time from the payroll index
// (companies/{cid}/payrollIndex/master.runs[]): a record (employeeId, dateYMD) is
//   - hard-locked  if a `completed` or `pending-approval` run covers it,
//   - soft-locked  if a `draft` run covers it,
//   - unlocked     if no run covers it.
// "Covers" = employeeId ∈ run.employeeIds AND run.periodStart ≤ dateYMD ≤ run.periodEnd
// (string compares on 'YYYY-MM-DD', per CLAUDE.md date rules). Consumers call
// deriveLockStatus per DATE — record rows and empty dates alike, and the add form
// for the date a new record would land on (PRD_TimeRecordAndPayFixes C5, which
// supersedes PRD_DtrWriteModel's Decision B "empty dates stay un-badged").

import { getDoc } from 'firebase/firestore';
import { payrollIndexRef } from './payrollIndexService';
import { runIncludesEmployee } from '../utils/payrollConflicts';

// Strongest-wins precedence; replaces the old imperative "never overwrite a
// different run's hard lock" guard (derivation is conflict-free).
const PRECEDENCE = { completed: 3, 'pending-approval': 2, draft: 1 };

// A run status that BLOCKS edits/OT (vs the soft 'draft' advisory lock). Single
// source so every consumer agrees on what "hard-locked" means (F9).
export const isHardLock = (status) => status === 'completed' || status === 'pending-approval';

// Fail-closed sentinel (C10a): when the index can't be read, consumers treat the
// record as hard-locked rather than render it editable. One definition so every
// consumer fails closed identically.
export const FAILCLOSED_LOCK = { status: 'completed', runId: null };

/**
 * Load the company's payroll-index run entries (one single-doc read).
 *
 * Errors are NOT swallowed to [] (unlike fetchPayrollIndex's advisory path): a
 * read failure must propagate so lock consumers can **fail closed** (C10a) — a
 * transient index-read error must never render a possibly-paid record editable.
 * A legitimately-absent index returns [] (a new company with no runs).
 *
 * @param {string} companyId
 * @returns {Promise<Array>} the `runs` entries ({ runId, periodStart, periodEnd, status, employeeIds })
 */
export async function loadActiveLocks(companyId) {
  const snap = await getDoc(payrollIndexRef(companyId));
  return snap.exists() ? (snap.data().runs || []) : [];
}

/**
 * Derive a record's lock from the index run entries.
 *
 * @param {string|number} employeeId
 * @param {string} dateYMD - the record's 'YYYY-MM-DD' date
 * @param {Array} runs - from loadActiveLocks
 * @returns {{ runId: string, status: 'completed'|'pending-approval'|'draft' } | null}
 */
export function deriveLockStatus(employeeId, dateYMD, runs) {
  if (employeeId == null || !dateYMD || !Array.isArray(runs)) return null;

  let best = null;
  for (const r of runs) {
    if (!r || !PRECEDENCE[r.status]) continue; // only draft/pending/completed
    // Skip a malformed entry rather than trust a comparison against an empty bound
    // (`'' <= date` is true), which would silently mis-derive coverage (F5).
    if (!r.periodStart || !r.periodEnd) continue;
    if (!runIncludesEmployee(r, employeeId)) continue;
    if (!(r.periodStart <= dateYMD && dateYMD <= r.periodEnd)) continue;
    if (!best || PRECEDENCE[r.status] > PRECEDENCE[best.status]) {
      best = { runId: r.runId, status: r.status };
    }
  }
  return best;
}
